import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { NavController, LoadingController, MenuController, PopoverController, AlertController } from '@ionic/angular';

import { Alert } from '../../services/alert';
import { UserService } from '../../services/user-service';
import { Util } from '../../services/util';
import { VibeToast } from 'src/app/services/vibe-toast';

import { MetaTagService } from 'src/app/services/meta-tag-service';
import { ObservableService } from 'src/app/services/observable-service';
import { PaymentTasks } from '../../services/payment-tasks';
import { Payment, Payout } from '../../models/payment';
import { BusinessLocation, BusinessTasks } from '../../services/business-tasks';

@Component({
  selector: 'app-business-payments',
  templateUrl: './business-payments.page.html',
  styleUrls: ['./business-payments.page.scss'],
})
export class BusinessPaymentsPage implements OnInit {

  businessLocations: BusinessLocation[]
  selectedBusinessLocationID?: string
  transfers?: Payment[]

  monthDate = Util.monthDate
  percentage = Util.formatPercentage
  formatCost = Util.formatCost

  availableAccountBalanceAmount?: number
  pendingAccountBalanceAmount?: number
  payouts?: Payout[]

  enableAutomaticPayouts: boolean = false

  payoutType = 'automatic'

  constructor(
    private navCtrl: NavController,
    private loadingCtrl: LoadingController,
    private alert: Alert,
    private alertCtrl: AlertController,
    private observables: ObservableService,
    private changeRef: ChangeDetectorRef,
    public userService: UserService,
    public toast: VibeToast,
    private menuCtrl: MenuController,
    private metaTagService: MetaTagService,
  ) {

    if (this.userService.user && !this.userService.user.type) {
      this.navCtrl.navigateForward(`/business-change-login`);
    }

    const business = this.userService.business;

    if (!business) {
      const sub = this.observables.businessFetched.subscribe(() => {
        sub.unsubscribe()
        this.loadContent()
      });
      return
    }

    this.loadContent()
  }

  ngOnInit() {}

  async loadContent() {

    if (!this.userService.business) return;
    this.pullBusinessLocations();
  }

  private async pullBusinessLocations() {

    let loading = await this.loadingCtrl.create()
    loading.present()

    let onlyIncludeWithStripeAccounts = true;

    BusinessTasks.pullBusinessLocations(onlyIncludeWithStripeAccounts, (businessLocations: BusinessLocation[]) => {

      if (!businessLocations.length) {
        this.error('Please first add a Business Location and associated Stripe Account through Settings.')
        return;
      }

      this.businessLocations = businessLocations
      this.selectedBusinessLocationID = businessLocations[0].id;
      this.changeRef.detectChanges()
      loading.dismiss();

      this.selectNewLocation()

    }, (err: string) => {
      loading.dismiss();
      if (!Util.isNavigationError(err)) {
        this.error(err)
      }
    })
  }

  private async pullBusinessTransfers() {
    if (!this.selectedBusinessLocationID) {
      this.error('Please select a Location.')
    }

    this.transfers = undefined;

    PaymentTasks.pullBusinessPayments(
      (transfers: Payment[]) => {

        this.transfers = transfers
        this.changeRef.detectChanges()

      }, (err: string) => {
        if (!Util.isNavigationError(err)) {
          this.error(err)
        }
    })
  }

  private async pullStripePayouts() {

    this.payouts = undefined;

    PaymentTasks.getStripeConnectAccountInfo_BusinessLocation(this.selectedBusinessLocationID, (
      balances: any,
      payouts: Payout[]) => {

      this.availableAccountBalanceAmount = balances.available
      this.pendingAccountBalanceAmount = balances.pending
      this.payouts = payouts
      this.changeRef.detectChanges()

    }, (err: string) => {
      if (!Util.isNavigationError(err)) {
        this.error(err)
      }
    })
  }

  async downloadTransfers() {

    const loading = await this.loadingCtrl.create()
    loading.present()

    let fields = {
      id: 'Payment ID',
      createdAt: 'Pay Date',
      apptDate: 'Service Date',
      type: 'Type',
      proName: 'Pro',
      name: 'Client',
      amount: 'Amount',
      currency: 'Currency',
      paid: 'Paid',
    }

    let name = 'shortcut-transfer-history'
    if (this.selectedBusinessLocationID) {
      let selectedLocationName = this.findNameOfLocation(this.selectedBusinessLocationID)
      name = `${name}-${selectedLocationName}`
    }

    this.downloadCSV(fields, this.transfers, name)

    loading.dismiss();
  }

  async downloadPayouts() {

    const loading = await this.loadingCtrl.create()
    loading.present()

    let fields = {
      created: 'Date sent',
      estArrivalDate: 'Est. arrival date',
      amountUSD: 'Amount',
      currency: 'Currency',
      status: 'Status',
      description: 'Type',
    }

    let name = 'stripe-payout-history'
    if (this.selectedBusinessLocationID) {
      let selectedLocationName = this.findNameOfLocation(this.selectedBusinessLocationID)
      name = `${name}-${selectedLocationName}`
    }

    this.downloadCSV(fields, this.payouts, name)

    loading.dismiss();
  }

  private findNameOfLocation(locationID: string) {
    for (const location of this.businessLocations) {
      if (location.id === locationID) {
        return location.name;
      }
    }
  }

  ionViewWillEnter() {
    this.metaTagService.updateTitle('Payments')
    this.loadContent()

    this.menuCtrl.enable(true);
  }

  ionViewDidEnter() {
    this.observables.publishSetCurrentMenuRoot('/payments')
  }

  interfaceOptions(headerText: string, subHeaderText: string) {
    return {
      header: headerText,
      subHeader: subHeaderText
    }
  }

  adminChat() {
    this.observables.publishOpenNewAdminChat();
  }

  async openStripe() {
    if (!this.selectedBusinessLocationID) {
      this.error('Please select a location.')
    }

    let loader = await this.loadingCtrl.create()
    loader.present()

    PaymentTasks.getStripeExpressLoginLink_BusinessLocation(this.selectedBusinessLocationID, (url: string) => {

      loader.dismiss()
      this.openPage(url);

    }, (errorMsg: string) => {
      loader.dismiss()
      this.error(errorMsg);
    })
  }

  selectNewLocation() {
    this.pullBusinessTransfers()
    this.pullStripePayouts()
  }

  stripeIDForSelectedBusiness() {
    return this.businessLocations.filter((l) => {return l.id === this.selectedBusinessLocationID})[0].stripeAccountID;
  }

  hasMultipleLocationsWithStripeAccount() {
    let stripeAccountID = this.stripeIDForSelectedBusiness();
    return this.businessLocations.filter((l) => {return l.stripeAccountID === stripeAccountID}).length > 1;
  }



  // Util

  private error(msg: string) {
    this.alert.show("Error", msg);
  }

  openPage(url: string) {
    window.open(url, '_blank')
  }


  // CSV

  private downloadCSV(fields: any, array: any[], name: string) {

    var rows = [Object.values(fields)];

    for (var i = 0; i < array.length; i++) {
      var a = array[i];

      var row = []
      for (let key of Object.keys(fields)) {
        // Replace all commas inside values.
        let val = a[key];
        row.push(val)
      }

      rows.push(row)
    }

    let csvContent = "data:text/csv;charset=utf-8,";
    rows.forEach(function(rowArray){
        let row = rowArray.join(",");
        csvContent += row + "\r\n";
    });

    var encodedUri = encodeURI(csvContent);

    var utcString = (new Date()).toUTCString().substring(5, 16)
    utcString.replace(' ', '')

    var link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `${name}-${utcString}.csv`);
        link.click()
  }

}
